import React, { useEffect, useState, useRef } from 'react'
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { questionManagementAPI } from '../../../services/api'
import { config } from '../../../config/env'

interface QuestionStats {
  total: number
  by_type: Record<string, number>
  by_category: Record<string, number>
  by_difficulty: Record<string, number>
}

interface Question {
  id: string
  title: string
  description: string
  category: string
  question_type: string
  difficulty_level: string
  marks: number
}

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories (no pre-fill)' },
  { value: 'engineering_tier1', label: 'Engineering Tier 1' },
  { value: 'engineering_tier2', label: 'Engineering Tier 2' },
  { value: 'management_tier1', label: 'Management Tier 1' },
]

const AdminQuestionsTab: React.FC = () => {
  const [stats, setStats] = useState<QuestionStats | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')
  
  // Pagination and Filtering
  const [page, setPage] = useState(1)
  const [totalQuestions, setTotalQuestions] = useState(0)
  const [search, setSearch] = useState('')
  const perPage = 50

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetchStats()
    fetchQuestions()
  }, [page, search])

  const fetchStats = async () => {
    try {
      const res = await questionManagementAPI.getStats()
      setStats(res.data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const fetchQuestions = async () => {
    try {
      setIsLoading(true)
      const res = await questionManagementAPI.getQuestions({
        page,
        per_page: perPage,
        search: search || undefined
      })
      setQuestions(res.data.questions)
      setTotalQuestions(res.data.total)
    } catch (error) {
      console.error('Failed to fetch questions:', error)
      toast.error('Failed to load questions')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return

    try {
      await questionManagementAPI.deleteQuestion(id)
      toast.success('Question deleted successfully')
      fetchStats()
      fetchQuestions()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete question')
    }
  }

  const handleDownloadTemplate = () => {
    // Build URL with optional category query param
    let templateUrl = `${config.API_URL}/questions/template`
    if (selectedCategory) {
      templateUrl += `?category=${encodeURIComponent(selectedCategory)}`
    }
    window.open(templateUrl, '_blank')
    setShowTemplateModal(false)
    setSelectedCategory('')
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)')
      return
    }

    try {
      setIsUploading(true)
      const res = await questionManagementAPI.bulkUpload(file)
      toast.success(res.data.message || 'Questions uploaded successfully')
      
      // Clear input and refresh
      if (fileInputRef.current) fileInputRef.current.value = ''
      
      // Reset to page 1 and fetch new data
      setPage(1)
      fetchStats()
      fetchQuestions()
    } catch (error: any) {
      console.error('Upload error:', error)
      const errorMessage = error.response?.data?.detail?.message || 
                          error.response?.data?.detail || 
                          'Failed to upload questions'
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage))
    } finally {
      setIsUploading(false)
    }
  }

  const totalPages = Math.ceil(totalQuestions / perPage)

  return (
    <div className="space-y-6 mt-6">
      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <DocumentTextIcon className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Questions</p>
              <p className="text-2xl font-semibold text-gray-900">{stats?.total || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <p className="text-sm font-medium text-gray-500 mb-2">By Difficulty</p>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Easy:</span>
              <span className="font-semibold text-green-600">{stats?.by_difficulty['easy'] || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Medium:</span>
              <span className="font-semibold text-yellow-600">{stats?.by_difficulty['medium'] || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Hard:</span>
              <span className="font-semibold text-red-600">{stats?.by_difficulty['hard'] || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 md:col-span-2 overflow-y-auto max-h-36">
          <p className="text-sm font-medium text-gray-500 mb-2">Top Categories</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {Object.entries(stats?.by_category || {})
              .sort((a, b) => b[1] - a[1])
              .map(([cat, count]) => (
                <div key={cat} className="flex justify-between text-sm border-b border-gray-100 pb-1">
                  <span className="text-gray-600 truncate mr-2" title={cat}>{cat}</span>
                  <span className="font-semibold text-indigo-600">{count}</span>
                </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk Upload Section */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Excel Bulk Upload</h3>
        <p className="text-gray-600 text-sm mb-6">
          Download the Excel template, fill in your questions, and upload the file to bulk insert questions into the database.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <button
            onClick={() => setShowTemplateModal(true)}
            className="btn btn-outline flex items-center bg-gray-50"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Download Template
          </button>
          
          <div className="hidden sm:block text-gray-300">|</div>
          
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="btn btn-primary flex items-center"
            >
              {isUploading ? (
                <span className="spinner mr-2"></span>
              ) : (
                <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
              )}
              {isUploading ? 'Uploading...' : 'Upload Excel File'}
            </button>
          </div>
        </div>
      </div>

      {/* Template Download Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Download Template
              </h3>
              <button
                onClick={() => {
                  setShowTemplateModal(false)
                  setSelectedCategory('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="input w-full"
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  The selected category will be pre-filled in the template. Dropdowns for category, question type, and difficulty level will be included in the Excel file.
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTemplateModal(false)
                    setSelectedCategory('')
                  }}
                  className="btn btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDownloadTemplate}
                  className="btn btn-primary flex-1 flex items-center justify-center"
                >
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Question Database</h3>
          <input
            type="text"
            placeholder="Search questions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input max-w-xs"
          />
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title / Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    <div className="spinner mx-auto mb-2"></div>
                    Loading questions...
                  </td>
                </tr>
              ) : questions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    No questions found.
                  </td>
                </tr>
              ) : (
                questions.map((q) => (
                  <tr key={q.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      #{q.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-md">
                        {q.title || (q.description && q.description.substring(0, 50) + '...')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {q.difficulty_level} • {q.marks} pts
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {q.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {q.question_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="text-red-600 hover:text-red-900 ml-4"
                        title="Delete question"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((page - 1) * perPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(page * perPage, totalQuestions)}</span> of{' '}
                  <span className="font-medium">{totalQuestions}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminQuestionsTab
